import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Badge } from '../../../../../components/ui/badge';
import { cn } from '@/utils/cn';
import { Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { LICENCE } from '../../../../../constants/index';
import { useMutation, useQuery } from 'react-query';
import { apiService } from '../../../../../utils/api';
import { toast } from 'sonner';
import { loadProfile } from '@/store/actions/auth';
import { loadCompanyDetail } from '@/store/actions/company';

const useGetEnabledLicences = (id: number | undefined) => {
  return useQuery({
    queryKey: ['licences', id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiService.get(`/api/v1/company/${id}/licences/`);
      return data;
    },
    refetchOnWindowFocus: false,
  });
};

interface ChangeLicenceModalProps {
  visible: boolean;
  onClose: () => void;
}

interface PlanCardProps {
  value: string;
  current: string | null | undefined;
  selected: boolean;
  onSelect: (value: string) => void;
  name: string;
  price: string;
  sub: string;
  features: string[];
  testId?: string;
}

const PlanCard = ({
  value,
  current,
  selected,
  onSelect,
  name,
  price,
  sub,
  features,
  testId,
}: PlanCardProps) => {
  const isCurrent = current === value;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onSelect(value)}
      className={cn(
        'relative text-left rounded-lg border p-5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
        selected
          ? 'border-primary-600 ring-1 ring-primary-600 bg-primary-50/40'
          : 'border-neutral-200 bg-white hover:border-neutral-300'
      )}
    >
      <span
        className={cn(
          'absolute top-4 right-4 h-4 w-4 rounded-full border flex items-center justify-center transition-colors',
          selected ? 'border-primary-600 bg-primary-600' : 'border-neutral-300 bg-white'
        )}
      >
        {selected && <Check className="h-[11px] w-[11px] text-white" strokeWidth={3} />}
      </span>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-neutral-900">{name}</span>
        {isCurrent && (
          <Badge variant="outline" size="sm">
            Aktuální
          </Badge>
        )}
      </div>

      <div className="flex items-baseline gap-1.5 mb-0.5">
        <span className="text-2xl font-bold text-neutral-900 tracking-tight">{price}</span>
        <span className="text-xs text-neutral-500">{sub}</span>
      </div>

      <ul className="mt-4 space-y-2">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-neutral-700">
            <Check className="h-[14px] w-[14px] text-primary-600 shrink-0" strokeWidth={2.5} />
            {f}
          </li>
        ))}
      </ul>
    </button>
  );
};

const ChangeLicenceModal = ({ visible, onClose }: ChangeLicenceModalProps) => {
  const detail = useSelector((state: any) => state?.company?.detail);
  const [licence, setLicence] = useState<string | null>(null);

  const { data: enabledLicences, isFetching } = useGetEnabledLicences(detail?.id);

  useEffect(() => {
    setLicence(detail?.licence?.licence_type);
  }, [detail, visible]);

  const dispatch = useDispatch();

  const changeLicence = useMutation({
    mutationFn: () => {
      return apiService.post(`/api/v1/company/${detail.id}/licence-plan-change/`, {
        licence_type: licence,
      });
    },
    onSuccess: () => {
      dispatch({ type: 'CHANGE_LICENCE', data: licence });
      dispatch(loadProfile());
      dispatch(loadCompanyDetail());
      toast.success('Licence úspěšně změněna');
      onClose();
    },
    onError: (error: any) => {
      console.error(error);
    },
  });

  if (!detail?.licence) {
    return null;
  }

  const currentType = detail?.licence?.licence_type;
  const payPrice = enabledLicences?.pay?.monthly_price_no_vat;
  const changed = licence !== currentType;
  const selectedFree = LICENCE[licence]?.name === 'free';

  const cta = !changed
    ? 'Vybraný plán je aktivní'
    : selectedFree
    ? 'Přejít na verzi zdarma'
    : `Přejít na placenou verzi ${payPrice ?? ''} Kč/měsíc`;

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="w-[760px] max-w-[92vw]">
        <DialogHeader>
          <DialogTitle>Výběr licence</DialogTitle>
          <DialogDescription>
            Změna se projeví od následujícího fakturačního období.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isFetching || !enabledLicences ? (
            <div className="flex justify-center items-center py-16">
              <SpinnerOverlay size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <PlanCard
                value="free"
                current={currentType}
                selected={selectedFree}
                onSelect={setLicence}
                name="Zdarma"
                price="0 Kč"
                sub="trvale"
                features={['Až 3 projekty', 'Neomezeně uživatelů', 'Základní podpora']}
                testId="licence-select-free-btn"
              />
              <PlanCard
                value="pay"
                current={currentType}
                selected={!selectedFree}
                onSelect={setLicence}
                name="Placená"
                price={`${payPrice ?? 0} Kč`}
                sub="/ osoba / měsíc"
                features={['Neomezeně projektů', 'Neomezeně uživatelů', 'Prioritní podpora']}
                testId="licence-select-pay-btn"
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" data-testid="licence-modal-close-btn" onClick={() => onClose()}>
            Zavřít
          </Button>
          <Button
            disabled={!changed || changeLicence.isLoading}
            data-testid="licence-modal-confirm-btn"
            onClick={() => changeLicence.mutate()}
          >
            {cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeLicenceModal;
